<template>
  <el-form
    v-if="form && formSchema?.properties"
    :model="form.fields"
    label-width="150px"
    label-position="top"
    require-asterisk-position="right"
    class="flex flex-col gap-3">
    <el-form-item
      v-for="(value, key) in formSchema.properties"
      :key="key"
      :prop="`form.fields.${key}`"
      :error="getErrorMessage(key)"
      :label="value.title"
      :required="formSchema.required?.includes(key)">
      <!-- Select Input -->
      <SmSelect
        v-if="isSelect(value)"
        v-model:selection="form.fields[key]"
        :label="value.title"
        :options="value.enum"
        class="w-full">
        <template #trigger="{ items }">
          <p>{{ items[0] }}</p>
        </template>
        <template #item="{ item }">
          <p>{{ item }}</p>
        </template>
      </SmSelect>

      <div v-else-if="isCheckbox(value)" class="flex items-center gap-6">
        <SmSwitch v-model="form.fields[key]" />
        <TextMultiline :text="value.title" />
      </div>

      <!-- Text Input -->
      <SmInput
        v-else
        v-model="form.fields[key]"
        outline
        :label="value.title"
        :type="value.format"
        design="form" />
    </el-form-item>
  </el-form>
</template>

<script lang="ts" setup>
  import { SoftwareIntegrationsService, SoftwareTypes } from '@/client'
  import useForm from '@/composables/useForm'
  import Jsonparser from '@/utils/formatter/jsonParser'
  import {
    isRequired,
    ValidationResult,
    validateEmail,
    ValidationRule,
  } from '@/components/sm/SmInput/SmInputValidator'

  const props = defineProps({
    applicationName: {
      type: String,
      required: true,
    },
    type: {
      type: Object as PropType<SoftwareTypes>,
      required: true,
    },
  })

  // ----------------------------------------------------------------------
  // Load schema
  // ----------------------------------------------------------------------

  type CredentialsSchema = {
    properties: Record<string, unknown>
    required?: string[]
  }

  const formSchema = ref<CredentialsSchema | null>(null)

  onMounted(async () => {
    await loadIntegration()
  })

  async function loadIntegration() {
    await SoftwareIntegrationsService.getIntegrationsApiV1SoftwareIntegrationsGet(
      { softwareName: props.applicationName, softwareType: props.type }
    ).then((res) => {
      formSchema.value = Jsonparser.deref(
        res[0].credentials_schema
      ) as CredentialsSchema

      if (formSchema.value?.properties) {
        type FieldDef = { format?: string }

        const entries = Object.entries(formSchema.value.properties)
        const passwordFields = entries.filter(
          ([, def]) => (def as FieldDef).format === 'password'
        )
        const otherFields = entries.filter(
          ([, def]) => (def as FieldDef).format !== 'password'
        )
        formSchema.value.properties = Object.fromEntries([
          ...otherFields,
          ...passwordFields,
        ])
      }
    })
  }

  // ----------------------------------------------------------------------
  // Form
  // ----------------------------------------------------------------------

  interface FieldDef {
    default?: unknown
  }

  const initialValues = computed((): Record<string, unknown> => {
    const vals: Record<string, unknown> = {}
    const schema = formSchema.value
    if (schema?.properties) {
      for (const [name, def] of Object.entries(schema.properties)) {
        const field = def as FieldDef

        if (isSelect(def)) {
          vals[name] = field.default !== undefined ? [field.default] : []
        } else {
          vals[name] = field.default ?? undefined
        }
      }
    }
    return vals
  })

  const validators = computed((): Record<string, ValidationRule[]> => {
    const vals: Record<string, ValidationRule[]> = {}
    const schema = formSchema.value
    if (schema?.properties) {
      for (const name of Object.keys(schema.properties)) {
        vals[name] = []
        if (schema.required?.includes(name)) {
          vals[name].push(isRequired)
        }
        if (name === 'email') {
          vals[name].push(validateEmail)
        }
      }
    }
    return vals
  })

  const form = defineModel<ReturnType<typeof useForm>>('formRef')

  watch(
    () => formSchema.value,
    (schema) => {
      if (!schema?.properties) return
      form.value = useForm(
        initialValues.value,
        {
          onReset: () => {
            /* ... */
          },
        },
        validators.value,
        {}
      )
    },
    { immediate: true }
  )

  const getErrorMessage = (fieldName: string): string => {
    const errorsRecord = form.value?.errors as
      | Record<string, ValidationResult[]>
      | undefined
    if (!errorsRecord) return ''

    const fieldErrors = errorsRecord[fieldName]
    if (!fieldErrors || fieldErrors.length === 0) return ''

    const first = fieldErrors[0]
    if (!first || typeof first.message !== 'string') return ''

    return first.message
  }

  // ----------------------------------------------------------------------
  // Check form type
  // ----------------------------------------------------------------------
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isSelect = (fieldDef: any) => {
    return fieldDef.enum && Array.isArray(fieldDef.enum)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isCheckbox = (fieldDef: any) => {
    return fieldDef.type === 'boolean'
  }
</script>
